import {type DateTime, type DateTimeFormatOptions, Settings} from "luxon";
import type {EnumTranslations, EnumType, TypeOfEnum} from "./EnumHelpers";
import {computedFromSignalOrValue, readSignalOrValue, type SignalOrValue} from "./SignalHelpers";
import type {NonOptionalNullable, Optional, OptionalNullable} from "./TypeHelpers";
import {isNonOptionalNullable} from "./TypeHelpers";
import {translateYesNo} from "./YesNoTranslation";

let defaultLocale: Optional<string>;

export function setDefaultLocale(locale: string): void {
  defaultLocale = locale;
  Settings.defaultLocale = locale;
}

export interface IValueFormatter<TValue> {
  format(value: TValue): string;
}

const stringValueFormatter: Readonly<IValueFormatter<string>> = {
  format: value => value,
};
const booleanValueFormatter: Readonly<IValueFormatter<boolean>> = {
  format: translateYesNo,
};

export type IntegerNumberFormatOptions = Intl.NumberFormatOptions & {
  style?: undefined,
  minimumFractionDigits?: 0,
  maximumFractionDigits?: 0,
};
export type FloatingPointNumberFormatOptions = Intl.NumberFormatOptions & {
  style?: Extract<Intl.NumberFormatOptionsStyle, "decimal">,
};
export type PercentageNumberFormatOptions = Intl.NumberFormatOptions & {
  style?: Extract<Intl.NumberFormatOptionsStyle, "percent">
};
export type PriceNumberFormatOptions = Intl.NumberFormatOptions & ({
  style?: Extract<Intl.NumberFormatOptionsStyle, "decimal">,
  currency?: undefined,
} | {
  style?: Extract<Intl.NumberFormatOptionsStyle, "currency">,
  currency?: string,
});

export function makeIntegerNumberOptions(options?: Readonly<IntegerNumberFormatOptions>): Intl.NumberFormatOptions {
  return {
    ...options,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  };
}

export function makeFloatingPointNumberOptions(
  options?: Readonly<FloatingPointNumberFormatOptions>,
): Intl.NumberFormatOptions {
  return {
    ...options,
    style: "decimal",
  };
}

export const ValueFormatters = {
  string: stringValueFormatter,
  boolean: booleanValueFormatter,

  number(format?: SignalOrValue<Optional<Readonly<Intl.NumberFormatOptions>>>,
         locale?: SignalOrValue<Optional<string>>): Readonly<IValueFormatter<number>> {
    return {
      format: value => value.toLocaleString(readSignalOrValue(locale) ?? defaultLocale, readSignalOrValue(format)),
    };
  },

  percent(format?: SignalOrValue<Optional<Readonly<PercentageNumberFormatOptions>>>,
          locale?: SignalOrValue<Optional<string>>): Readonly<IValueFormatter<number>> {
    return ValueFormatters.number(computedFromSignalOrValue(format, format => ({
      ...(format ?? {}),
      style: "percent",
    })), locale);
  },

  price(format?: SignalOrValue<Optional<Readonly<PriceNumberFormatOptions>>>,
        locale?: SignalOrValue<Optional<string>>): Readonly<IValueFormatter<number>> {
    return ValueFormatters.number(computedFromSignalOrValue(format, format => {
      const options: PriceNumberFormatOptions = {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
        ...(format ?? {}),
      };
      if (options.style === undefined && options.currency !== undefined)
        options.style = "currency";
      return options;
    }), locale);
  },

  dateTime(format?: SignalOrValue<Optional<Readonly<DateTimeFormatOptions>>>,
           locale?: SignalOrValue<Optional<string>>): Readonly<IValueFormatter<DateTime>> {
    return {
      format: value => value.toLocaleString(readSignalOrValue(format), {
        locale: readSignalOrValue(locale) ?? defaultLocale,
      }),
    };
  },

  forEnum<TEnumType extends EnumType, TEnum extends TypeOfEnum<TEnumType>>(
    translations: EnumTranslations<TEnum>,
  ): Readonly<IValueFormatter<TEnum>> {
    return {
      format: value => translations[value],
    };
  },

  optionalNullable<TValue>(formatter: Readonly<IValueFormatter<NonOptionalNullable<TValue>>>,
                           fallback: SignalOrValue<string> = ""): Readonly<IValueFormatter<OptionalNullable<TValue>>> {
    return {
      format: value => isNonOptionalNullable(value)
        ? formatter.format(value)
        : readSignalOrValue(fallback),
    };
  },
};
